import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { TTherapistAvailability } from "../../../../services/types/user.types";
import styles from "../../appointmentsPage.module.css";

const DAY_IDS = [1, 2, 3, 4, 5, 6, 7] as const; // Sun(1) -> Sat(7)

const DAY_OPTIONS: { id: number; key: string }[] = [
  { id: 1, key: "APPOINTMENTS_DAY_SUNDAY" },
  { id: 2, key: "APPOINTMENTS_DAY_MONDAY" },
  { id: 3, key: "APPOINTMENTS_DAY_TUESDAY" },
  { id: 4, key: "APPOINTMENTS_DAY_WEDNESDAY" },
  { id: 5, key: "APPOINTMENTS_DAY_THURSDAY" },
  { id: 6, key: "APPOINTMENTS_DAY_FRIDAY" },
  { id: 7, key: "APPOINTMENTS_DAY_SATURDAY" },
];

function getAvailabilityByTherapistAndDay(
  availability: TTherapistAvailability[],
  therapistId: number,
  dayId: number
): TTherapistAvailability[] {
  return availability.filter((a) => {
    const availTherapistId = a.therapistsId ?? (a as any).TherapistsId;
    const availDayId = a.dayId ?? (a as any).DayId;
    const availIsAvailable = a.isAvailable ?? (a as any).IsAvailable;
    const availSpecificDate = a.specificDate ?? (a as any).SpecificDate;
    return (
      availTherapistId === therapistId &&
      availDayId === dayId &&
      availIsAvailable &&
      !availSpecificDate
    );
  });
}

/** Strip seconds from time string - always display HH:mm only */
function formatTimeToHHmm(time: string): string {
  if (!time) return "";
  const parts = time.split(":");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return time;
}

/** Check if slot [sStart, sEnd] overlaps with [fStart, fEnd]. Handles HH:mm and HH:mm:ss. */
function slotsOverlap(
  sStart: string,
  sEnd: string,
  fStart: string,
  fEnd: string
): boolean {
  if (!fStart || !fEnd) return true;
  const ns = formatTimeToHHmm(sStart);
  const ne = formatTimeToHHmm(sEnd);
  const nf = formatTimeToHHmm(fStart);
  const nfe = formatTimeToHHmm(fEnd);
  return ns < nfe && ne > nf;
}

/**
 * TherapistsAvailability Component
 *
 * Displays a weekly grid of all therapists' availability with dynamic filters.
 */
const TherapistsAvailability: React.FC = () => {
  const { therapistsList, allTherapistsAvailability, companiesOptions, filters, setFilters } =
    useAppointmentsContext();

  const [activeOnly, setActiveOnly] = useState(true);
  const [viewMode, setViewMode] = useState<"therapist" | "time">("time");
  const [selectedDayIds, setSelectedDayIds] = useState<number[]>([]);
  const [selectedTherapistIds, setSelectedTherapistIds] = useState<number[]>(
    []
  );
  const [filterStartTime, setFilterStartTime] = useState("");
  const [filterEndTime, setFilterEndTime] = useState("");

  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showDaysDropdown, setShowDaysDropdown] = useState(false);
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [daysSearch, setDaysSearch] = useState("");
  const [therapistSearch, setTherapistSearch] = useState("");
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const daysDropdownRef = useRef<HTMLDivElement>(null);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCompanyDropdown(false);
      }
      if (
        daysDropdownRef.current &&
        !daysDropdownRef.current.contains(event.target as Node)
      ) {
        setShowDaysDropdown(false);
      }
      if (
        therapistDropdownRef.current &&
        !therapistDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTherapistDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const baseTherapists = useMemo(() => {
    if (activeOnly) return therapistsList.filter((t) => t.isActive);
    return therapistsList;
  }, [therapistsList, activeOnly]);

  const filteredTherapists = useMemo(() => {
    if (selectedTherapistIds.length === 0) return baseTherapists;
    return baseTherapists.filter((t) => selectedTherapistIds.includes(t.id));
  }, [baseTherapists, selectedTherapistIds]);

  const displayedDayIds = useMemo(() => {
    if (selectedDayIds.length === 0) return [...DAY_IDS];
    return DAY_IDS.filter((d) => selectedDayIds.includes(d));
  }, [selectedDayIds]);

  const hasTimeFilter = !!(filterStartTime && filterEndTime);

  const getCellSlots = useMemo(() => {
    return (therapistId: number, dayId: number): string[] => {
      const slots = getAvailabilityByTherapistAndDay(
        allTherapistsAvailability,
        therapistId,
        dayId
      );
      const formatSlot = (s: TTherapistAvailability) => {
        const start = s.startTime ?? (s as any).StartTime ?? "";
        const end = s.endTime ?? (s as any).EndTime ?? "";
        return `${formatTimeToHHmm(start)}–${formatTimeToHHmm(end)}`;
      };
      if (!hasTimeFilter) {
        return slots.map(formatSlot);
      }
      return slots
        .filter((s) => {
          const start = s.startTime ?? (s as any).StartTime ?? "";
          const end = s.endTime ?? (s as any).EndTime ?? "";
          return slotsOverlap(start, end, filterStartTime, filterEndTime);
        })
        .map(formatSlot);
    };
  }, [
    allTherapistsAvailability,
    hasTimeFilter,
    filterStartTime,
    filterEndTime,
  ]);

  const getTherapistsForTimeSlot = useCallback((dayId: number, slotStart: string, slotEnd: string) => {
    const result: { id: number; name: string; exactTime: string }[] = [];
    
    for (const therapist of filteredTherapists) {
      const availabilities = getAvailabilityByTherapistAndDay(
        allTherapistsAvailability,
        therapist.id,
        dayId
      );
      
      const matched = availabilities.filter(a => {
        const aStart = a.startTime ?? (a as any).StartTime ?? "";
        const aEnd = a.endTime ?? (a as any).EndTime ?? "";
        
        if (hasTimeFilter && !slotsOverlap(aStart, aEnd, filterStartTime, filterEndTime)) {
          return false;
        }

        return slotsOverlap(aStart, aEnd, slotStart, slotEnd);
      });

      if (matched.length > 0) {
        const exactTimes = matched.map(m => {
          const s = formatTimeToHHmm(m.startTime ?? (m as any).StartTime ?? "");
          const e = formatTimeToHHmm(m.endTime ?? (m as any).EndTime ?? "");
          return `${s}-${e}`;
        }).join(", ");

        result.push({ id: therapist.id, name: therapist.name, exactTime: exactTimes });
      }
    }
    
    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [filteredTherapists, allTherapistsAvailability, hasTimeFilter, filterStartTime, filterEndTime]);

  const activeTimeSlots = useMemo(() => {
    if (viewMode !== "time") return [];
    
    const hours = Array.from({length: 24}, (_, i) => {
      const s = `${i.toString().padStart(2, "0")}:00`;
      const endStr = i === 23 ? "23:59" : `${(i + 1).toString().padStart(2, "0")}:00`;
      return { start: s, end: endStr, label: `${s} - ${endStr}` };
    });

    return hours.filter(slot => {
      return displayedDayIds.some(dayId => {
        return getTherapistsForTimeSlot(dayId, slot.start, slot.end).length > 0;
      });
    });
  }, [viewMode, displayedDayIds, getTherapistsForTimeSlot]);

  const sortedTherapists = useMemo(() => {
    const withSlots: typeof filteredTherapists = [];
    const withoutSlots: typeof filteredTherapists = [];
    for (const therapist of filteredTherapists) {
      const hasAny = displayedDayIds.some(
        (dayId) => getCellSlots(therapist.id, dayId).length > 0
      );
      if (hasAny) withSlots.push(therapist);
      else withoutSlots.push(therapist);
    }
    return [...withSlots, ...withoutSlots];
  }, [filteredTherapists, displayedDayIds, getCellSlots]);

  const handleDayToggle = (dayId: number) => {
    setSelectedDayIds((prev) => {
      const isSelected = prev.includes(dayId);
      const next = isSelected
        ? prev.filter((id) => id !== dayId)
        : [...prev, dayId];
      return next;
    });
  };

  const handleTherapistToggle = (therapistId: number) => {
    setSelectedTherapistIds((prev) => {
      const isSelected = prev.includes(therapistId);
      const next = isSelected
        ? prev.filter((id) => id !== therapistId)
        : [...prev, therapistId];
      return next;
    });
  };

  const handleClearFilters = () => {
    setSelectedDayIds([]);
    setSelectedTherapistIds([]);
    setFilterStartTime("");
    setFilterEndTime("");
    setCompanySearch("");
    setDaysSearch("");
    setTherapistSearch("");
    setFilters({ ...filters, fakeCompanyIds: undefined });
  };

  const filteredCompaniesForDropdown = useMemo(() => {
    if (!companySearch.trim()) return companiesOptions;
    const q = companySearch.toLowerCase();
    return companiesOptions.filter((c) => c.name.toLowerCase().includes(q));
  }, [companiesOptions, companySearch]);

  const filteredDaysForDropdown = useMemo(() => {
    if (!daysSearch.trim()) return DAY_OPTIONS;
    const q = daysSearch.toLowerCase();
    return DAY_OPTIONS.filter((d) =>
      t(d.key).toLowerCase().includes(q)
    );
  }, [daysSearch]);

  const filteredTherapistsForDropdown = useMemo(() => {
    if (!therapistSearch.trim()) return baseTherapists;
    const q = therapistSearch.toLowerCase();
    return baseTherapists.filter((t) => t.name.toLowerCase().includes(q));
  }, [baseTherapists, therapistSearch]);

  const selectedDays = DAY_OPTIONS.filter((d) =>
    selectedDayIds.includes(d.id)
  );
  const selectedTherapists = baseTherapists.filter((t) =>
    selectedTherapistIds.includes(t.id)
  );
  
  const selectedCompanyIds = filters.fakeCompanyIds || [];
  const selectedCompanies = companiesOptions.filter((c) =>
    selectedCompanyIds.includes(c.id)
  );

  const handleCompanyToggle = (companyId: number) => {
    const currentIds = filters.fakeCompanyIds || [];
    const isSelected = currentIds.includes(companyId);
    const newIds = isSelected
      ? currentIds.filter((id) => id !== companyId)
      : [...currentIds, companyId];
    setFilters({ ...filters, fakeCompanyIds: newIds.length > 0 ? newIds : undefined });
  };

  const handleRemoveCompany = (companyId: number) => {
    const currentIds = filters.fakeCompanyIds || [];
    const newIds = currentIds.filter((id) => id !== companyId);
    setFilters({ ...filters, fakeCompanyIds: newIds.length > 0 ? newIds : undefined });
  };

  if (baseTherapists.length === 0) {
    return (
      <div className={styles.therapistsAvailabilityContainer}>
        <div className={styles.therapistsAvailabilityHeader}>
          <h1 className={styles.therapistsAvailabilityTitle}>
            {t("APPOINTMENTS_TAB_THERAPISTS_AVAILABILITY")}
          </h1>
        </div>
        <div className={styles.therapistsAvailabilityEmpty}>
          <span className={styles.therapistsAvailabilityEmptyIcon}>👥</span>
          <p className={styles.therapistsAvailabilityEmptyText}>
            {t("THERAPISTS_AVAILABILITY_NO_DATA")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.therapistsAvailabilityContainer}>
      <div className={styles.therapistsAvailabilityHeader}>
        <h1 className={styles.therapistsAvailabilityTitle}>
          {t("APPOINTMENTS_TAB_THERAPISTS_AVAILABILITY")}
        </h1>
        <label className={styles.therapistsAvailabilityToggle}>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          <span>{t("THERAPISTS_AVAILABILITY_ACTIVE_ONLY")}</span>
        </label>
      </div>

      {/* Filters - same style as Calendar tab */}
      <div className={styles.filtersSection}>
        <div className={styles.filtersGrid}>
          {/* Days Filter */}
          <div className={styles.filterGroup} ref={daysDropdownRef}>
            <label className={styles.filterLabel}>
              {t("THERAPISTS_AVAILABILITY_FILTER_DAYS")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${
                  showDaysDropdown ? styles.customSelectActive : ""
                }`}
                onClick={() => setShowDaysDropdown(!showDaysDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {selectedDays.length === 0 ? (
                    <>
                      <span className={styles.therapistIcon}>📅</span>
                      {t("THERAPISTS_AVAILABILITY_ALL_DAYS")}
                    </>
                  ) : (
                    <span className={styles.therapistCount}>
                      {selectedDays.length} {t("THERAPISTS_AVAILABILITY_DAYS_SELECTED")}
                    </span>
                  )}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showDaysDropdown && (
                <div className={styles.customSelectDropdown}>
                  {selectedDays.length > 0 && (
                    <div className={styles.selectedTags}>
                      {selectedDays.map((d) => (
                        <div key={d.id} className={styles.tag}>
                          <span className={styles.tagIcon}>📅</span>
                          <span className={styles.tagText}>{t(d.key)}</span>
                          <button
                            className={styles.tagRemove}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDayToggle(d.id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.dropdownSearch}>
                    <input
                      type="text"
                      className={styles.dropdownSearchInput}
                      placeholder={t("THERAPISTS_AVAILABILITY_SEARCH_DAYS")}
                      value={daysSearch}
                      onChange={(e) => setDaysSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {selectedDays.length > 0 && (
                      <div
                        className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                        onClick={() => {
                          setSelectedDayIds([]);
                          setDaysSearch("");
                        }}
                      >
                        <span className={styles.optionIcon}>🔄</span>
                        <span className={styles.optionText}>
                          {t("THERAPISTS_AVAILABILITY_CLEAR_DAYS")}
                        </span>
                      </div>
                    )}
                    {filteredDaysForDropdown.length === 0 ? (
                      <div className={styles.dropdownEmpty}>
                        {t("THERAPISTS_AVAILABILITY_NO_DAYS_FOUND")}
                      </div>
                    ) : (
                      filteredDaysForDropdown.map((d) => (
                      <div
                        key={d.id}
                        className={`${styles.dropdownOption} ${
                          selectedDayIds.includes(d.id)
                            ? styles.dropdownOptionSelected
                            : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDayToggle(d.id);
                        }}
                      >
                        <span className={styles.optionIcon}>📅</span>
                        <span className={styles.optionText}>{t(d.key)}</span>
                        {selectedDayIds.includes(d.id) && (
                          <span className={styles.checkmark}>✓</span>
                        )}
                      </div>
                    ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Company Filter */}
          <div className={styles.filterGroup} ref={companyDropdownRef}>
            <label className={styles.filterLabel}>
              {t("APPOINTMENTS_FILTER_COMPANY")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${
                  showCompanyDropdown ? styles.customSelectActive : ""
                }`}
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {selectedCompanies.length === 0 ? (
                    <>
                      <span className={styles.companyIcon}>🏢</span>
                      {t("APPOINTMENTS_ALL_COMPANIES")}
                    </>
                  ) : (
                    <span className={styles.companyCount}>
                      {selectedCompanies.length} {t("APPOINTMENTS_COMPANIES_SELECTED")}
                    </span>
                  )}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showCompanyDropdown && (
                <div className={styles.customSelectDropdown}>
                  {selectedCompanies.length > 0 && (
                    <div className={styles.selectedTags}>
                      {selectedCompanies.map((company) => (
                        <div key={company.id} className={styles.tag}>
                          <span className={styles.tagIcon}>🏢</span>
                          <span className={styles.tagText}>{company.name}</span>
                          <button
                            className={styles.tagRemove}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCompany(company.id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.dropdownSearch}>
                    <input
                      type="text"
                      className={styles.dropdownSearchInput}
                      placeholder={t("APPOINTMENTS_SEARCH_COMPANY")}
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {selectedCompanies.length > 0 && (
                      <div
                        className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                        onClick={() => {
                          setFilters({ ...filters, fakeCompanyIds: undefined });
                          setCompanySearch("");
                        }}
                      >
                        <span className={styles.optionIcon}>🔄</span>
                        <span className={styles.optionText}>
                          {t("APPOINTMENTS_CLEAR_COMPANIES")}
                        </span>
                      </div>
                    )}
                    {filteredCompaniesForDropdown.length === 0 ? (
                      <div className={styles.dropdownEmpty}>
                        {t("APPOINTMENTS_NO_COMPANIES_FOUND")}
                      </div>
                    ) : (
                      filteredCompaniesForDropdown.map((company) => (
                        <div
                          key={company.id}
                          className={`${styles.dropdownOption} ${
                            selectedCompanyIds.includes(company.id)
                              ? styles.dropdownOptionSelected
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompanyToggle(company.id);
                          }}
                        >
                          <span className={styles.optionIcon}>🏢</span>
                          <span className={styles.optionText}>{company.name}</span>
                          {selectedCompanyIds.includes(company.id) && (
                            <span className={styles.checkmark}>✓</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Therapist Filter */}
          <div className={styles.filterGroup} ref={therapistDropdownRef}>
            <label className={styles.filterLabel}>
              {t("THERAPISTS_AVAILABILITY_FILTER_THERAPIST")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${
                  showTherapistDropdown ? styles.customSelectActive : ""
                }`}
                onClick={() =>
                  setShowTherapistDropdown(!showTherapistDropdown)
                }
              >
                <span className={styles.customSelectValue}>
                  {selectedTherapists.length === 0 ? (
                    <>
                      <span className={styles.therapistIcon}>👥</span>
                      {t("THERAPISTS_AVAILABILITY_ALL_THERAPISTS")}
                    </>
                  ) : (
                    <span className={styles.therapistCount}>
                      {selectedTherapists.length}{" "}
                      {t("THERAPISTS_AVAILABILITY_THERAPISTS_SELECTED")}
                    </span>
                  )}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showTherapistDropdown && (
                <div className={styles.customSelectDropdown}>
                  {selectedTherapists.length > 0 && (
                    <div className={styles.selectedTags}>
                      {selectedTherapists.map((th) => (
                        <div key={th.id} className={styles.tag}>
                          <span className={styles.tagIcon}>👨‍⚕️</span>
                          <span className={styles.tagText}>{th.name}</span>
                          <button
                            className={styles.tagRemove}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTherapistToggle(th.id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.dropdownSearch}>
                    <input
                      type="text"
                      className={styles.dropdownSearchInput}
                      placeholder={t("THERAPISTS_AVAILABILITY_SEARCH_THERAPIST")}
                      value={therapistSearch}
                      onChange={(e) => setTherapistSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {selectedTherapists.length > 0 && (
                      <div
                        className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                        onClick={() => {
                          setSelectedTherapistIds([]);
                          setTherapistSearch("");
                        }}
                      >
                        <span className={styles.optionIcon}>🔄</span>
                        <span className={styles.optionText}>
                          {t("THERAPISTS_AVAILABILITY_CLEAR_THERAPISTS")}
                        </span>
                      </div>
                    )}
                    {filteredTherapistsForDropdown.length === 0 ? (
                      <div className={styles.dropdownEmpty}>
                        {t("THERAPISTS_AVAILABILITY_NO_THERAPISTS_FOUND")}
                      </div>
                    ) : (
                      filteredTherapistsForDropdown.map((therapist) => (
                        <div
                          key={therapist.id}
                          className={`${styles.dropdownOption} ${
                            selectedTherapistIds.includes(therapist.id)
                              ? styles.dropdownOptionSelected
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTherapistToggle(therapist.id);
                          }}
                        >
                          <span className={styles.optionIcon}>👨‍⚕️</span>
                          <span className={styles.optionText}>
                            {therapist.name}
                          </span>
                          {selectedTherapistIds.includes(therapist.id) && (
                            <span className={styles.checkmark}>✓</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Time Filter */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              {t("THERAPISTS_AVAILABILITY_FILTER_TIME")}
            </label>
            <div className={styles.therapistsAvailabilityTimeRow}>
              <input
                type="time"
                step="60"
                className={styles.filterInput}
                value={filterStartTime}
                onChange={(e) => setFilterStartTime(e.target.value)}
              />
              <span className={styles.therapistsAvailabilityTimeSeparator}>
                –
              </span>
              <input
                type="time"
                step="60"
                className={styles.filterInput}
                value={filterEndTime}
                onChange={(e) => setFilterEndTime(e.target.value)}
              />
              {(filterStartTime || filterEndTime) && (
                <button
                  type="button"
                  className={styles.therapistsAvailabilityTimeResetBtn}
                  onClick={() => {
                    setFilterStartTime("");
                    setFilterEndTime("");
                  }}
                  title={t("THERAPISTS_AVAILABILITY_RESET_TIME")}
                  aria-label={t("THERAPISTS_AVAILABILITY_RESET_TIME")}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* View Mode */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>
              {t("THERAPISTS_AVAILABILITY_SORT_BY")}
            </label>
            <div className={styles.sortModeButtons}>
              <button
                className={`${styles.sortModeBtn} ${
                  viewMode === "time" ? styles.sortModeBtnActive : ""
                }`}
                onClick={() => setViewMode("time")}
              >
                {t("THERAPISTS_AVAILABILITY_SORT_TIME")}
              </button>
              <button
                className={`${styles.sortModeBtn} ${
                  viewMode === "therapist" ? styles.sortModeBtnActive : ""
                }`}
                onClick={() => setViewMode("therapist")}
              >
                {t("THERAPISTS_AVAILABILITY_SORT_THERAPIST")}
              </button>
            </div>
          </div>

          <div className={styles.filterActions}>
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleClearFilters}
            >
              {t("APPOINTMENTS_CLEAR_FILTER")}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.therapistsAvailabilityTableWrapper}>
        <table className={styles.therapistsAvailabilityTable}>
          <thead>
            <tr>
              <th className={styles.therapistsAvailabilityTherapistCol}>
                {viewMode === "therapist" ? t("THERAPIST_AVAILABILITY") : t("THERAPISTS_AVAILABILITY_FILTER_TIME")}
              </th>
              {displayedDayIds.map((dayId) => (
                <th
                  key={dayId}
                  className={styles.therapistsAvailabilityDayCol}
                >
                  {t(DAY_OPTIONS[dayId - 1].key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {viewMode === "therapist" ? (
              sortedTherapists.map((therapist) => (
                <tr
                  key={therapist.id}
                  className={
                    !therapist.isActive
                      ? styles.therapistsAvailabilityRowInactive
                      : ""
                  }
                >
                  <td className={styles.therapistsAvailabilityTherapistCell}>
                    {therapist.name}
                  </td>
                  {displayedDayIds.map((dayId) => {
                    const slots = getCellSlots(therapist.id, dayId);
                    return (
                      <td
                        key={dayId}
                        className={styles.therapistsAvailabilityCell}
                      >
                        {slots.length > 0 ? (
                          <div className={styles.therapistsAvailabilityBadges}>
                            {slots.map((slot, idx) => (
                              <span
                                key={idx}
                                className={styles.therapistsAvailabilityBadge}
                              >
                                {slot}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.therapistsAvailabilityEmptyCell}>
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              activeTimeSlots.map((slot, idx) => (
                <tr key={idx}>
                  <td className={styles.therapistsAvailabilityTherapistCell}>
                    <span style={{ fontWeight: 600 }}>{slot.label}</span>
                  </td>
                  {displayedDayIds.map((dayId) => {
                    const availableTherapists = getTherapistsForTimeSlot(dayId, slot.start, slot.end);
                    return (
                      <td key={dayId} className={styles.therapistsAvailabilityCell}>
                        {availableTherapists.length > 0 ? (
                          <div className={styles.therapistsAvailabilityBadges}>
                            {availableTherapists.map((tItem) => (
                              <span
                                key={tItem.id}
                                className={styles.therapistsAvailabilityBadge}
                                title={`${tItem.name} זמין: ${tItem.exactTime}`}
                                style={{ cursor: "help" }}
                              >
                                {tItem.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.therapistsAvailabilityEmptyCell}>
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TherapistsAvailability;
